"use client";

import { useAutoScroll } from "../../hooks/use-auto-scroll";
import { MessageBubble } from "./message-bubble";
import { hasReplyText, isToolLoading, isVisibleToolPart } from "./message-parts";
import { TypingIndicator } from "./typing-indicator";

interface Message {
  id: string;
  role: "user" | "assistant";
  parts: { type: string; [key: string]: unknown }[];
}

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
}

/**
 * The assistant turn is still streaming but has nothing moving on screen: every tool call has
 * finished and no reply text has arrived yet.
 */
function isAwaitingReply(message: Message | undefined): boolean {
  if (message?.role !== "assistant") return false;
  const toolParts = message.parts.filter(isVisibleToolPart);
  return !hasReplyText(message.parts) && toolParts.length > 0 && !toolParts.some(isToolLoading);
}

/**
 * The assistant turn exists but MessageBubble renders nothing for it yet (no reply text, no visible
 * tool call — e.g. only a logEvent so far), so the avatar typing row stands in for it.
 */
function isInvisibleAssistantTurn(message: Message | undefined): boolean {
  return message?.role === "assistant" && !hasReplyText(message.parts) && !message.parts.some(isVisibleToolPart);
}

export function MessageList({ messages, isLoading }: MessageListProps) {
  const { containerRef, handleScroll } = useAutoScroll([messages, isLoading]);
  const last = messages[messages.length - 1];

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="flex-1 overflow-y-auto"
    >
      <div className="mx-auto max-w-3xl pb-2">
        {messages.map((message) => (
          <MessageBubble
            key={message.id}
            role={message.role}
            parts={message.parts}
          />
        ))}
        {isLoading && isAwaitingReply(last) && (
          // Under the tool-call rows, indented to the assistant bubble column (avatar 32px + gap 12px)
          <div className="flex px-4 pb-3 pl-[60px]">
            <TypingIndicator />
          </div>
        )}
        {isLoading && (last?.role === "user" || isInvisibleAssistantTurn(last)) && (
          <div className="flex gap-3 px-4 py-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <span className="text-xs font-medium">AI</span>
            </div>
            <TypingIndicator />
          </div>
        )}
      </div>
    </div>
  );
}
